/* =========================================
   SHARED SITE CONSTANTS
   Single source of truth for navigation and the demo CTA.
   The cal.com link is the only conversion path on the site;
   change it here and nowhere else.
   ========================================= */

export const CAL_URL = 'https://cal.com/aaron-c/user'

/* =========================================
   THE IA TOGGLE  (NEXT_PUBLIC_SITE_IA)

   `current` (default, and anything unrecognised) is the site exactly as
   it shipped. `focused` narrows the story: three verticals drop out of
   every navigation surface, and two products carry their new names.

   ⚠ THIS IS A BUILD-TIME FLAG, NOT A RUNTIME SWITCH. `[industry]` sets
   `dynamicParams = false`, `redirects()` is serialized into the route
   manifest, and llms.txt is `force-static`. Flipping the var in Vercel
   requires a redeploy; there is no request-time path.

   ⚠ `focused` HIDES, IT DOES NOT DELETE. INDUSTRIES in
   content/industries.ts stays whole in both modes, so every industry
   page still builds, still sits in the sitemap, and still ranks — the
   flag only decides what the NAV advertises. That is deliberate and
   matches the standing rule for /money-transmitter at CLAUDE.md:17:
   a real ranking page is never deleted, only reordered or hidden.
   Deleting /debt-collection would also fail the build, because
   blog/lib.ts throws on a post topic with no matching industry and
   debt-collection-city-licenses.mdx declares that topic.

   Note on the literal-env-read rule (CLAUDE.md:118): that rule exists
   so DRAFT CONTENT cannot survive dead-code elimination into the
   production bundle. Nothing is excluded for secrecy here — both link
   sets are public marketing copy and ship either way — so the flag is
   read once, centrally, and the filtered arrays are what consumers
   import. Do not "fix" this into a per-site literal; there is nothing
   to keep out of the bundle.
   ========================================= */
export const SITE_IA: 'current' | 'focused' =
  process.env.NEXT_PUBLIC_SITE_IA === 'focused' ? 'focused' : 'current'

/* Hidden from navigation in `focused`, by href. The PAGES stay live.
   Chosen on measured Search Console data (3 months to 2026-10-06):
   together these three carry 689 impressions and 2 clicks, so hiding
   them costs almost nothing — but DELETING them would forfeit
   /debt-collection's 37-query cluster, which is the largest non-brand
   cluster the site has and sits at an average position of 29.5. */
const HIDDEN_IN_FOCUSED: readonly string[] = [
  '/debt-collection',
  '/fintech',
  '/crypto',
]

/* Product route + display name per mode, keyed by the STABLE INTERNAL
   KEY. The key never changes: `--accent-maintain` and `--accent-seats`
   are reused for unrelated meanings in changelog.module.css ("new" and
   "security" badges) and docs.module.css (4xx chips), so renaming the
   keys or the CSS tokens would break styling nothing in this file
   touches. Only the href and the label move. */
export const PRODUCT_ROUTES = {
  apply: { href: '/apply', label: 'Apply' },
  seats:
    SITE_IA === 'focused'
      ? { href: '/officers', label: 'Officers' }
      : { href: '/seats', label: 'Seats' },
  maintain:
    SITE_IA === 'focused'
      ? { href: '/renewals', label: 'Renewals' }
      : { href: '/maintain', label: 'Maintain' },
  api: { href: '/api', label: 'API' },
  firms: { href: '/firms', label: 'Firms' },
} as const

export type ProductRouteKey = keyof typeof PRODUCT_ROUTES

/* Both product paths exist as routes in BOTH modes, so flipping the
   flag can never 404 a link someone already shared. The inactive path
   canonicalizes to the active one (see maintain/ and seats/ page.tsx),
   which is what keeps the pair from reading as duplicate content.
   When the naming is finally settled, replace the inactive directory
   with a 301 in next.config.mjs and delete this note. */
export const PRODUCT_LINKS = [
  PRODUCT_ROUTES.apply,
  PRODUCT_ROUTES.seats,
  PRODUCT_ROUTES.maintain,
  PRODUCT_ROUTES.api,
  PRODUCT_ROUTES.firms,
]

/* Order is positioning, not alphabetics. Mortgage and insurance lead
   (per-person licensing is the focus), then lending and collections,
   then the payments verticals. This array feeds the nav dropdown, the
   homepage industry strip, the About grid, the 404 directory, the demo
   form's org-type list and llms.txt, so filtering it here moves all
   six at once and no consumer needs to know the flag exists.
   Keep in step with INDUSTRIES in content/industries.ts. */
const ALL_INDUSTRY_LINKS = [
  { href: '/mortgage', label: 'Mortgage' },
  { href: '/insurance', label: 'Insurance' },
  { href: '/lending', label: 'Lending' },
  { href: '/debt-collection', label: 'Collections' },
  { href: '/fintech', label: 'Fintech' },
  { href: '/crypto', label: 'Crypto' },
  { href: '/money-transmitter', label: 'Money Transmitter' },
] as const

export const INDUSTRY_LINKS =
  SITE_IA === 'focused'
    ? ALL_INDUSTRY_LINKS.filter((l) => !HIDDEN_IN_FOCUSED.includes(l.href))
    : [...ALL_INDUSTRY_LINKS]

/* The Company dropdown: everything that is not a product or an
   industry. Security is NOT here: it sits as its own top-level link
   to the right of Company, where a compliance buyer expects it. */
export const COMPANY_LINKS = [
  /* About leads the menu: it is the page that answers "who is this"
     for a first-time visitor, and the one a search engine or an
     assistant reads to resolve the company. */
  { href: '/about', label: 'About' },
  /* Docs (developer docs + the MCP connector) lives here, one of the two
     top-nav homes the owner chose for it (2026-09); the old top-level
     "Reference" dropdown was removed at the same time. */
  { href: '/docs', label: 'Docs' },
  { href: '/blog', label: 'Blog' },
  /* FAQ sits in Company rather than beside the product pages: it
     answers "who is this and how does it work", which is the same
     job About does, not a thing being sold. */
  { href: '/faq', label: 'FAQ' },
  { href: '/contact', label: 'Contact' },
] as const
